import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganizationList } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useGraphStorageApi } from '../api/useGraphStorageApi';
import { GraphEditor } from './GraphEditor';

export const GraphEditorLayout: React.FC = () => {
    const { graphId } = useParams<{ graphId: string }>();
    const navigate = useNavigate();
    const { getGraph } = useGraphStorageApi();
    const { setActive, isLoaded: isOrgListLoaded } = useOrganizationList();

    const { data: graphData, error: graphError } = useQuery({
        queryKey: ['graphOrgId', graphId],
        queryFn: () => getGraph(graphId!),
        enabled: !!graphId,
        select: data => ({ orgId: data.orgId }),
    });

    const [ready, setReady] = useState(false);

    // Redirect on non-recoverable fetch errors (not found, forbidden), but not on transient 401s
    useEffect(() => {
        if (!graphError) return;
        const status = (graphError as { response?: { status?: number } }).response?.status;
        if (status !== 401) {
            navigate('/');
        }
    }, [graphError, navigate]);

    // Once we know the graph's orgId + Clerk is loaded, switch context to match
    useEffect(() => {
        if (!graphData || !isOrgListLoaded || !setActive) return;

        setActive({ organization: graphData.orgId })
            .then(() => setReady(true))
            .catch(() => navigate('/'));
    }, [graphData, isOrgListLoaded, setActive, navigate]);

    if (!graphId || !ready) {
        return null;
    }

    return <GraphEditor graphId={graphId} />;
};
